export const OPERATOR_OP_OPTIONS = [
  {
    value: 'AND',
    pill: 'Dan',
    description: 'Semua bahan di kelompok ini harus digunakan bersamaan.',
  },
  {
    value: 'XOR',
    pill: 'Salah satu',
    description: 'Bahan saling menggantikan (hanya boleh pakai tepat 1 bahan).',
  },
  {
    value: 'OR',
    pill: 'Atau',
    description: 'Bisa menggunakan satu atau kombinasi dari bahan-bahan di kelompok ini.',
  },
]

export const OPERATOR_JOIN_OPTIONS = [
  {
    value: 'AND',
    pill: 'Dan',
    description: 'Seluruh kelompok aturan harus dipenuhi bersamaan (standar produksi).',
  },
  {
    value: 'OR',
    pill: 'Atau',
    description: 'Cukup memenuhi salah satu kelompok aturan produksi.',
  },
  {
    value: 'XOR',
    pill: 'Salah satu',
    description: 'Hanya boleh memilih tepat satu kelompok aturan produksi.',
  },
]

export const createDefaultGroup = (op = 'OR') => ({
  id: `g-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  op,
})

export const createInitialRule = () => ({
  join: 'AND',
  groups: [createDefaultGroup()],
})

export const validateSchemaRule = (rule, inputItemIds = []) => {
  if (!rule) return { isValid: true, errors: [] }
  const errors = []
  const validIds = inputItemIds.filter((id) => id !== null && id !== undefined && id !== '')
  const groups = rule.groups || []

  if (groups.length === 0) {
    return { isValid: false, errors: ['Minimal harus ada 1 kelompok aturan bahan.'] }
  }

  const seenIds = new Set()
  groups.forEach((g, idx) => {
    if (!['AND', 'OR', 'XOR'].includes(g.op)) {
      errors.push(`Kelompok #${idx + 1}: operator tidak valid (${g.op}).`)
    }
    if (!g.itemIds || g.itemIds.length === 0) {
      errors.push(`Kelompok #${idx + 1} belum memiliki bahan baku.`)
    } else {
      g.itemIds.forEach((id) => {
        if (seenIds.has(id)) {
          errors.push(`Bahan #${id} duplikat (hanya boleh masuk ke 1 kelompok).`)
        }
        seenIds.add(id)
        if (!validIds.includes(id)) {
          errors.push(`Bahan #${id} tidak ditemukan di daftar bahan baku.`)
        }
      })
    }
  })

  const unassigned = validIds.filter((id) => !seenIds.has(id))
  if (unassigned.length > 0) {
    errors.push(`Terdapat ${unassigned.length} bahan baku yang belum masuk kelompok aturan.`)
  }

  return { isValid: errors.length === 0, errors, unassignedIds: unassigned }
}

// Wording mirrors the operator pills (Dan / Atau / Salah satu) so the preview reads like the builder
const joinNames = (names, op) =>
  op === 'XOR' ? `salah satu dari (${names.join(', ')})` : `(${names.join(op === 'AND' ? ' dan ' : ' atau ')})`

const GROUP_MEANING = {
  AND: 'semua bahan wajib dipakai',
  OR: 'minimal 1 bahan dipakai, boleh campur',
  XOR: 'pakai tepat 1 bahan (substitusi)',
}

const JOIN_MEANING = {
  AND: 'semua kelompok wajib terpenuhi',
  OR: 'minimal 1 kelompok terpenuhi',
  XOR: 'tepat 1 kelompok yang dipakai',
}

export const generateHumanReadableSummary = (rule, getItemLabel) => {
  if (!rule?.groups?.length) {
    return {
      formula: 'Tanpa aturan khusus',
      explanation: 'Semua bahan baku berlaku bebas (standar).',
    }
  }

  const getLabel = (id) => (typeof getItemLabel === 'function' && getItemLabel(id)) || `Bahan #${id}`
  const multi = rule.groups.length > 1

  const groupParts = rule.groups.map((g, idx) => {
    const names = (g.itemIds || []).map(getLabel)
    if (!names.length) return `[Kelompok ${idx + 1}: kosong]`
    if (names.length === 1) return names[0]
    return joinNames(names, g.op)
  })

  // Uppercase join words separate the group level from the lowercase words inside groups
  const formula = !multi
    ? groupParts[0]
    : rule.join === 'XOR'
      ? `SALAH SATU dari [${groupParts.join(', ')}]`
      : groupParts.join(rule.join === 'OR' ? ' ATAU ' : ' DAN ')

  const groupSentences = rule.groups
    .filter((g) => (g.itemIds || []).length > 1)
    .map((g) => {
      const meaning = GROUP_MEANING[g.op] || GROUP_MEANING.AND
      return multi ? `Kelompok ${rule.groups.indexOf(g) + 1}: ${meaning}` : meaning
    })
  if (multi) groupSentences.push(`Antar kelompok: ${JOIN_MEANING[rule.join] || JOIN_MEANING.AND}`)

  const explanation = groupSentences.length
    ? `${groupSentences.join('. ')}.`.replace(/^./, (c) => c.toUpperCase())
    : 'Bahan ini wajib dipakai.'

  return { formula, explanation }
}

// Rule as stored by the backend may be a JSON string; returns null when there is no usable rule
export const normalizeRule = (raw) => {
  let rule = raw
  if (typeof rule === 'string') {
    try {
      rule = JSON.parse(rule)
    } catch {
      return null
    }
  }
  if (!rule?.groups?.length) return null
  return {
    join: String(rule.join || 'AND').toUpperCase(),
    groups: rule.groups.map((g) => ({
      op: String(g.op || 'AND').toUpperCase(),
      itemIds: (g.itemIds || []).map(Number),
    })),
  }
}

// Mirrors the backend's evaluateInputCombination so the modal blocks what the API would reject
export const evaluateRule = (rule, suppliedItemIds) => {
  const supplied = new Set(suppliedItemIds.map(Number))
  const groups = rule.groups.map((g) => {
    const present = g.itemIds.filter((id) => supplied.has(id)).length
    if (g.op === 'AND') {
      const missing = g.itemIds.length - present
      return missing === 0
        ? { ok: true }
        : { ok: false, missCount: missing, message: 'Semua bahan pada kelompok ini wajib dipilih.' }
    }
    if (g.op === 'OR') {
      return present >= 1
        ? { ok: true }
        : { ok: false, missCount: 1, message: 'Minimal satu bahan pada kelompok ini harus dipilih.' }
    }
    if (present === 1) return { ok: true }
    return present === 0
      ? { ok: false, missCount: 1, message: 'Salah satu bahan pada kelompok ini harus dipilih.' }
      : { ok: false, missCount: present - 1, message: 'Hanya boleh memilih salah satu bahan pada kelompok ini.' }
  })

  const passed = groups.filter((g) => g.ok).length
  const ok =
    rule.join === 'AND' ? passed === groups.length : rule.join === 'OR' ? passed >= 1 : passed === 1
  if (ok) return { ok, groups }
  if (passed === groups.length) {
    return { ok, groups, message: 'Hanya boleh memenuhi salah satu kelompok.' }
  }
  // Point at the failed group closest to being satisfied, like the backend does
  const failedIdx = groups
    .map((g, i) => i)
    .filter((i) => !groups[i].ok)
    .sort((a, b) => groups[a].missCount - groups[b].missCount)[0]
  const prefix = groups.length > 1 ? `Kelompok ${failedIdx + 1}: ` : ''
  return { ok, groups, message: `${prefix}${groups[failedIdx].message}` }
}

// shortcut: the mapping API doesn't say which schema an item uses, so pick the schema whose inputs best match
// the recipe items; upgrade once the backend returns the rule (and schema units) with possibleItems
export const pickSchema = (schemaDetails, recipeItemIds) => {
  const wanted = new Set(recipeItemIds.map(Number))
  const score = (schema) => {
    const ids = (schema?.input || []).map((inp) => Number(inp.itemId))
    const hits = ids.filter((id) => wanted.has(id)).length
    return 2 * hits - ids.length - wanted.size // 0 when the input set equals the recipe set
  }
  return schemaDetails.filter(Boolean).sort((a, b) => score(b) - score(a))[0] || null
}

